/**
 * Advanced Search Filters Component
 *
 * Collapsible filter panel for advanced search options:
 * - Filter by channel
 * - Filter by date range
 * - Filter by attachments/links presence
 *
 * Inspired by Viber/WhatsApp UX patterns
 */

import { useState, useEffect } from 'react';
import type { SearchFilters, User } from '@/types/api.types';
import { useChannels } from '@/hooks/useChannels';
import { useUsers } from '@/hooks/useUsers';

interface AdvancedFiltersProps {
  filters: Partial<SearchFilters>;
  onFiltersChange: (filters: Partial<SearchFilters>) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export const AdvancedFilters = ({
  filters,
  onFiltersChange,
  isOpen,
  onToggle,
}: AdvancedFiltersProps) => {
  // Fetch user's accessible channels and all users
  const { data: channels, isLoading: channelsLoading } = useChannels();
  const { data: users, isLoading: usersLoading } = useUsers();

  // Local state for filter inputs
  const [selectedChannelId, setSelectedChannelId] = useState(filters.channelId || '');
  const [selectedUserId, setSelectedUserId] = useState(filters.userId || '');
  const [dateFrom, setDateFrom] = useState(filters.dateFrom || '');
  const [dateTo, setDateTo] = useState(filters.dateTo || '');
  const [hasAttachments, setHasAttachments] = useState(filters.hasAttachments || false);
  const [hasLinks, setHasLinks] = useState(filters.hasLinks || false);

  // Count active filters
  const activeFilterCount = [
    filters.channelId,
    filters.userId,
    filters.dateFrom,
    filters.dateTo,
    filters.hasAttachments,
    filters.hasLinks,
  ].filter(Boolean).length;

  // Update parent when local state changes
  useEffect(() => {
    const newFilters: Partial<SearchFilters> = {
      ...filters,
    };

    if (selectedChannelId) newFilters.channelId = selectedChannelId;
    if (selectedUserId) newFilters.userId = selectedUserId;
    if (dateFrom) newFilters.dateFrom = dateFrom;
    if (dateTo) newFilters.dateTo = dateTo;
    if (hasAttachments) newFilters.hasAttachments = hasAttachments;
    if (hasLinks) newFilters.hasLinks = hasLinks;

    onFiltersChange(newFilters);
    // Intentionally keyed only on the local controls: including `filters`/`onFiltersChange`
    // would feed the parent's update back in and loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId, selectedUserId, dateFrom, dateTo, hasAttachments, hasLinks]);

  const handleClearFilters = () => {
    setSelectedChannelId('');
    setSelectedUserId('');
    setDateFrom('');
    setDateTo('');
    setHasAttachments(false);
    setHasLinks(false);
    onFiltersChange({});
  };

  return (
    <div className="border-b border-gray-200">
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            Разширени филтри
          </span>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {activeFilterCount}
            </span>
          )}
        </div>
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
      </button>

      {/* Filter Panel */}
      {isOpen && (
        <div className="px-4 py-3 bg-gray-50 space-y-3">
          {/* Channel Selector */}
          <div className="space-y-2">
            <label htmlFor="channel-filter" className="block text-xs font-medium text-gray-700">
              Канал
            </label>
            <select
              id="channel-filter"
              value={selectedChannelId}
              onChange={(e) => setSelectedChannelId(e.target.value)}
              disabled={channelsLoading}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Всички канали</option>
              {channels?.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  #{channel.name}
                  {channel.type === 'private' && ' 🔒'}
                  {channel.type === 'announcement' && ' 📢'}
                </option>
              ))}
            </select>
          </div>

          {/* User/Author Selector */}
          <div className="space-y-2">
            <label htmlFor="user-filter" className="block text-xs font-medium text-gray-700">
              Автор на съобщението
            </label>
            <select
              id="user-filter"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={usersLoading}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Всички потребители</option>
              {users?.users?.map((user: User) => (
                <option key={user.id} value={user.id}>
                  {user.display_name || `${user.first_name} ${user.last_name}`}
                  {(user.role === 'admin' || user.role === 'super_admin') && ' 👑'}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              Период
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="date-from" className="sr-only">От дата</label>
                <input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="От дата"
                />
              </div>
              <div>
                <label htmlFor="date-to" className="sr-only">До дата</label>
                <input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="До дата"
                />
              </div>
            </div>
          </div>

          {/* Content Type Filters */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              Тип съдържание
            </label>
            <div className="space-y-2">
              {/* Has Attachments */}
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasAttachments}
                  onChange={(e) => setHasAttachments(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 flex items-center space-x-1">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <span>Само с файлове</span>
                </span>
              </label>

              {/* Has Links */}
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasLinks}
                  onChange={(e) => setHasLinks(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 flex items-center space-x-1">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <span>Само с линкове</span>
                </span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <button
              onClick={handleClearFilters}
              disabled={activeFilterCount === 0}
              className="text-xs text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Изчисти филтри
            </button>
            <div className="text-xs text-gray-500">
              {activeFilterCount > 0 && (
                <span>{activeFilterCount} активни филтра</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
